'use client'

import { compactClassName } from '@/components/homepage-october/compact'
import { DestinationLink } from '../blocks/DestinationLink'

import * as React from 'react'
import { Archive, BadgePercent, Beer, Columns2, Columns3, Cuboid, CupSoda, Droplets, FileText, Hourglass, LayoutPanelTop, MapPin, Minimize2, PanelBottom, PanelTop, RectangleVertical, Refrigerator, Ruler, Snowflake, Wine, Zap, type LucideIcon } from '@/components/homepage-october/lucide'

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/homepage-october/ui/accordion'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/homepage-october/ui/badge'
import { Button } from '@/components/homepage-october/ui/button'
import { Card } from '@/components/homepage-october/ui/card'
import { Label } from '@/components/ui/label'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { cn } from '@/lib/utils'
import { ArrowRight } from '../blocks/icons'
import { Img } from '../blocks/Img'
import { ChipRows, Section, SectionHeader, SubHeader, words } from '../blocks/layout'
import type { Model } from '../types'

/* ------------------------------------------------------------------ shared bits */

/** A link-styled label for whole-card links: the model's words, with the trailing arrow drawn as an icon. */
function More({ text, className }: { text: string; className?: string }) {
  return (
    <span className={compactClassName(cn('inline-flex items-center gap-1.5 font-semibold text-[#0454c0]', className))}>
      {words(text)}
      <ArrowRight aria-hidden="true" className="size-4 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" />
    </span>
  )
}

/* ------------------------------------------------------------------ V18 local: power costs and rebates */

type Rate = Model['local']['rates'][number]
const cents = (rate: string) => Number.parseFloat(rate) || 0

// Electricity-rate colour scale: green where power is cheap, amber in the middle, the brand orange where it costs
// most, over 13¢–30¢ (Hawaii's 52.7¢ caps at orange). Light tints behind, dark ink on top: every one of the 51
// rates measures at least 4.9:1 ink on tint.
const LO = 13
const HI = 30
const RAMP = {
  bar: ['#12b76a', '#f5b000', '#ff9951'],
  tint: ['#ecfbf2', '#fff8e1', '#fff1e6'],
  ink: ['#067647', '#8a5300', '#b54708'],
} as const
function mix(a: string, b: string, t: number) {
  const ch = (h: string, i: number) => Number.parseInt(h.slice(1 + i * 2, 3 + i * 2), 16)
  return '#' + [0, 1, 2].map((i) => Math.round(ch(a, i) + (ch(b, i) - ch(a, i)) * t).toString(16).padStart(2, '0')).join('')
}
function ramp(stops: readonly [string, string, string], t: number) {
  return t <= 0.5 ? mix(stops[0], stops[1], t * 2) : mix(stops[1], stops[2], (t - 0.5) * 2)
}
function rateTone(rate: number) {
  const t = Math.min(1, Math.max(0, (rate - LO) / (HI - LO)))
  return { bar: ramp(RAMP.bar, t), tint: ramp(RAMP.tint, t), ink: ramp(RAMP.ink, t) }
}

/** "Your state": a native select over all 51 rates; shows the chosen state's rate big and links to its own page. */
type Basis = NonNullable<Model['local']['basis']>
/** Dollars a month at a state's rate: yearly kWh × ¢/kWh ÷ 100 ÷ 12. */
const perMonth = (kwh: number | null, rate: number) => (kwh ? Math.round((kwh * rate) / 100 / 12) : null)

function StatePicker({ rates, value, onPick, basis }: { rates: Rate[]; value: string; onPick: (href: string) => void; basis?: Basis }) {
  const id = React.useId()
  const r = rates.find((x) => x.href === value) ?? rates[0]
  const nums = rates.map((x) => cents(x.rate))
  const min = Math.min(...nums)
  const max = Math.max(...nums)
  const lo = rates[nums.indexOf(min)]
  const hi = rates[nums.indexOf(max)]
  const at = (v: number) => (max > min ? Math.min(100, Math.max(0, ((v - min) / (max - min)) * 100)) : 0)
  const n = cents(r.rate)
  const tone = rateTone(n)
  const track = `linear-gradient(90deg, ${RAMP.bar[0]} ${at(LO)}%, ${RAMP.bar[1]} ${at((LO + HI) / 2)}%, ${RAMP.bar[2]} ${at(HI)}%, ${RAMP.bar[2]} 100%)`
  return (
    <div className={compactClassName("flex h-full flex-col")}>
      <Label htmlFor={id} className="gap-1.5! text-xs! font-semibold! uppercase tracking-[0.12em] text-[#05603a]">
        <Zap aria-hidden="true" className="size-3.5" />
        Your state
      </Label>
      <div className={compactClassName("mt-3 [&>[data-slot=native-select-wrapper]]:w-full!")}>
        <NativeSelect
          id={id}
          value={r.href}
          onChange={(e) => onPick(e.target.value)}
          className="h-12! rounded-xl! border-[#10a65c]/35! bg-white! pl-4! text-base! font-semibold text-neutral-900 shadow-sm!"
        >
          {rates.map((x) => (
            <NativeSelectOption key={x.href} value={x.href}>{x.name}</NativeSelectOption>
          ))}
        </NativeSelect>
      </div>
      <div aria-live="polite" aria-atomic="true" className={compactClassName("mt-5")}>
        <p className={compactClassName("tabular text-5xl font-bold leading-none tracking-tight")} style={{ color: tone.ink }}>{r.rate}</p>
        <p className={compactClassName("mt-2 text-sm text-neutral-700")}>Average home electricity rate in {r.name}</p>
      </div>
      {/* What the rate means in dollars, and how (owner, 10/15/2026): one fridge, one freezer, both, with the arithmetic. */}
      {basis?.fridgeKwh ? (
        <div className={compactClassName("mt-4 rounded-xl bg-white/85 p-3 ring-1 ring-black/5")}>
          <dl className={compactClassName("grid grid-cols-3 gap-2 text-center")}>
            {[['One refrigerator', perMonth(basis.fridgeKwh, n)], ['One freezer', perMonth(basis.freezerKwh, n)], ['Fridge + freezer', (perMonth(basis.fridgeKwh, n) ?? 0) + (perMonth(basis.freezerKwh, n) ?? 0)]].map(([k, v]) => (
              <div key={String(k)}>
                <dt className={compactClassName("text-[11px] leading-tight text-neutral-600")}>{k}</dt>
                <dd className={compactClassName("tabular mt-0.5 text-lg font-bold text-neutral-950")}>{v == null ? '—' : `$${v}`}<span className={compactClassName("text-xs font-medium text-neutral-500")}>/mo</span></dd>
              </div>
            ))}
          </dl>
          <p className={compactClassName("mt-2.5 text-[11px] leading-relaxed text-neutral-600")}>
            How: kWh a year × {r.rate} ÷ 12. A typical full-size refrigerator on this page uses {basis.fridgeKwh} kWh a year (the median of the {basis.fridgeN} with a listed figure); a freezer {basis.freezerKwh} kWh (the {basis.freezerN} listed). Two refrigerators: double it. Each model’s own figure is on its page.{' '}
            <DestinationLink href={basis.calculator} className="font-semibold text-[#0454c0] hover:underline">Energy cost calculator →</DestinationLink>
          </p>
        </div>
      ) : null}
      <div aria-hidden="true" className={compactClassName("mt-5")}>
        <div className={compactClassName("relative h-2.5 rounded-full ring-1 ring-black/5")} style={{ backgroundImage: track }}>
          <span
            className={compactClassName("absolute top-1/2 size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-md ring-1 ring-black/15 transition-[left] duration-300 motion-reduce:transition-none")}
            style={{ left: `${at(n)}%`, backgroundColor: tone.bar }}
          />
        </div>
        <div className={compactClassName("mt-2 flex justify-between gap-3 text-[11px] font-medium text-neutral-600")}>
          <span>{lo.name} {lo.rate}</span>
          <span>{hi.name} {hi.rate}</span>
        </div>
      </div>
      <div className={compactClassName("mt-auto pt-6")}>
        <Button asChild variant="ctaSecondary" className="h-auto! min-h-11 w-full whitespace-normal! rounded-full! px-5! py-2.5! text-center font-semibold!">
          <DestinationLink href={r.href}>
            Rebates &amp; energy costs in {r.name}
            <ArrowRight aria-hidden="true" />
          </DestinationLink>
        </Button>
      </div>
    </div>
  )
}

/**
 * One state's rate as a tinted tile: the name on its own line (so "Massachusetts" never collides with the rate at
 * 360 px), then a bar proportional to the rate (Hawaii, the highest, fills it) beside the rate itself.
 */
function RateTile({ r, max, active, kwh }: { r: Rate; max: number; active: boolean; kwh?: number | null }) {
  const n = cents(r.rate)
  const tone = rateTone(n)
  return (
    <li>
      <DestinationLink
        href={r.href}
        data-active={active || undefined}
        style={{ backgroundColor: tone.tint, '--rate-bar': tone.bar } as React.CSSProperties}
        className="group flex h-full flex-col justify-between gap-1.5 rounded-xl border border-black/[0.06] px-3 py-2.5 transition duration-200 hover:-translate-y-0.5 hover:border-[color:var(--rate-bar)] hover:shadow-md data-[active]:ring-2 data-[active]:ring-[#0469f0] data-[active]:ring-offset-2 data-[active]:ring-offset-[#f0faf4]"
      >
        <span className={compactClassName("text-[13px] font-medium leading-snug text-neutral-800 group-hover:text-neutral-950")}>{r.name}{kwh ? <span className={compactClassName("block text-[11px] font-normal text-neutral-600")}>{`≈ $${perMonth(kwh, n)}/mo for a refrigerator`}</span> : null}</span>
        <span className={compactClassName("flex items-center gap-2")}>
          <span aria-hidden="true" className={compactClassName("block h-1.5 flex-1 overflow-hidden rounded-full bg-white/90 ring-1 ring-black/[0.04]")}>
            <span className={compactClassName("block h-full rounded-full")} style={{ width: `${Math.max(4, (n / max) * 100)}%`, backgroundColor: tone.bar }} />
          </span>
          <span className={compactClassName("tabular shrink-0 text-[13px] font-bold")} style={{ color: tone.ink }}>{r.rate}</span>
        </span>
      </DestinationLink>
    </li>
  )
}

export function Local({ model }: { model: Model }) {
  const m = model.local
  const [picked, setPicked] = React.useState(m.rates[0]?.href ?? '')
  const [touched, setTouched] = React.useState(false)
  const max = Math.max(1, ...m.rates.map((r) => cents(r.rate)))
  return (
    <Section aria={m.aria} v2="V18" tone="mint">
      <SectionHeader head={m.head} tone="mint" />
      <Card className="gap-0! overflow-hidden border-[#10a65c]/20 py-0! shadow-[0_24px_60px_-34px_rgba(16,166,92,0.45)]!">
        <div className={compactClassName("grid lg:grid-cols-[minmax(0,1fr)_360px]")}>
          <div className={compactClassName("p-5 sm:p-6 md:p-8")}>
            <h3 className={compactClassName("text-xl font-semibold tracking-tight text-neutral-950 md:text-2xl")}>{m.title}</h3>
            <p className={compactClassName("mt-2 max-w-2xl text-pretty leading-relaxed text-neutral-600")}>{m.text}</p>
            <p className={compactClassName("mt-6 flex items-center gap-2 text-sm font-semibold text-neutral-900")}>
              <MapPin aria-hidden="true" className="size-4 shrink-0 text-[#0469f0]" />
              {m.chipsLabel}
            </p>
            <ul className={compactClassName("mt-3 flex flex-wrap gap-1.5")}>
              {m.states.map((s) => (
                <li key={s.href}>
                  <DestinationLink
                    href={s.href}
                    className="inline-flex items-center gap-1.5 rounded-full border bg-white py-1 pr-3 pl-1.5 text-[13px] font-medium text-neutral-800 shadow-xs transition duration-200 hover:-translate-y-0.5 hover:border-[#10a65c]/50 hover:text-[#05603a] hover:shadow-sm"
                  >
                    <img src={s.flag.src} alt={s.flag.alt} width={24} height={16} loading="lazy" decoding="async" className={compactClassName("h-4 w-6 shrink-0 rounded-[3px] object-cover ring-1 ring-black/10")} />
                    {s.name}
                  </DestinationLink>
                </li>
              ))}
            </ul>
            <DestinationLink href={m.all.href} aria-label={m.all.aria} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0454c0] hover:underline hover:underline-offset-2">
              {words(m.all.text)}
              <ArrowRight aria-hidden="true" className="size-4" />
            </DestinationLink>
          </div>
          {m.rates.length ? (
            <div className={compactClassName("border-t border-[#10a65c]/15 bg-gradient-to-br from-[#e4f7ec] via-[#f2fbf6] to-white p-5 sm:p-6 md:p-8 lg:border-t-0 lg:border-l")}>
              <StatePicker rates={m.rates} basis={m.basis} value={picked} onPick={(h) => { setPicked(h); setTouched(true) }} />
            </div>
          ) : null}
        </div>
      </Card>
      <SubHeader title={m.ratesTitle} link={m.ratesLink} className="mt-8 md:mt-10" />
      <ul className={compactClassName("grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8")}>
        {m.rates.map((r) => (
          <RateTile key={r.href} r={r} max={max} active={touched && r.href === picked} kwh={m.basis?.fridgeKwh} />
        ))}
      </ul>
    </Section>
  )
}

/* ------------------------------------------------------------------ V19 quick answers */

type FaqEntry = Model['faq']['items'][number]

function FaqItem({ it, value }: { it: FaqEntry; value: string }) {
  return (
    <AccordionItem
      value={value}
      className={cn(
        'group/faq rounded-xl border border-b! bg-white shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-[#0469f0]/30 hover:shadow-md data-[state=open]:border-[#0469f0]/35 data-[state=open]:shadow-md',
        // Every answer and link stays in the server HTML (forceMount below). A closed panel is display:none, so it is
        // not seen, read or focusable. The stock height animation needs a re-measure that forceMount skips, so it is
        // switched off and the opened answer rises in instead (v3-rise; none under reduced motion).
        '[&>[data-slot=accordion-content][data-state=closed]]:hidden! [&>[data-slot=accordion-content]]:animate-none!',
      )}
    >
      <AccordionTrigger className="items-start! gap-3! rounded-xl! px-4 py-4! text-[15px]! font-semibold! leading-snug text-neutral-950 hover:text-[#0454c0] sm:px-5 [&>svg]:mt-1.5 [&>svg]:size-5! [&>svg]:text-[#0469f0]!">
        <span className={compactClassName("min-w-0 flex-1")}>{it.q}</span>
      </AccordionTrigger>
      <AccordionContent forceMount className="px-4 pb-5! sm:px-5 motion-safe:animate-v3-rise">
        <p className={compactClassName("text-[15px] leading-relaxed text-neutral-600")}>{it.a}{it.fridge ? <> <span className={compactClassName("text-neutral-800")}>{it.fridge}</span></> : null}</p>
        {it.link ? (
          <DestinationLink href={it.link.href} aria-label={it.link.aria} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0454c0] hover:underline hover:underline-offset-2">
            {words(it.link.text)}
            <ArrowRight aria-hidden="true" className="size-4" />
          </DestinationLink>
        ) : null}
      </AccordionContent>
    </AccordionItem>
  )
}

export function Faq({ model }: { model: Model }) {
  const m = model.faq
  const half = Math.ceil(m.items.length / 2)
  const cols = [m.items.slice(0, half), m.items.slice(half)]
  return (
    <Section aria={m.aria} v2="V19" tone="white">
      <div aria-hidden="true" className={compactClassName("pointer-events-none absolute inset-0 overflow-hidden")}>
        <div className={compactClassName("absolute -top-40 -right-32 size-[520px] rounded-full bg-[radial-gradient(circle,rgba(4,105,240,0.10),transparent_65%)]")} />
        <div className={compactClassName("absolute -bottom-48 -left-40 size-[480px] rounded-full bg-[radial-gradient(circle,rgba(255,153,81,0.12),transparent_65%)]")} />
      </div>
      <div className={compactClassName("relative")}>
        <SectionHeader head={m.head} tone="white" />
        {/* One accordion (arrow keys move through all 13), laid out as two columns that each read top to bottom. */}
        <Accordion type="multiple" className="grid items-start gap-3 lg:grid-cols-2 lg:gap-x-6">
          {cols.map((col, c) => (
            <div key={c} className={compactClassName("grid content-start gap-3")}>
              {col.map((it, k) => (
                <FaqItem key={it.q} it={it} value={`faq-${c * half + k}`} />
              ))}
            </div>
          ))}
        </Accordion>
        <ChipRows rows={m.chiprows} />
      </div>
    </Section>
  )
}

/* ------------------------------------------------------------------ V20 refrigeration A–Z */

// One lucide shape per entry (owner 10/15/2026: "we need better icons, more professional"), drawn in the entry's tint:
// a French door is the top-panel layout turned over (two doors up, one drawer down), a side-by-side two columns, top-
// and bottom-freezer the panel on that side, a chest freezer the box with a lid.
const WIKI_ICON: [RegExp, LucideIcon, string?][] = [
  [/french/i, LayoutPanelTop, 'rotate-180'],
  [/side-by-side/i, Columns2],
  [/top-freezer/i, PanelTop],
  [/bottom-freezer/i, PanelBottom],
  [/compact/i, Minimize2],
  [/built-in/i, Columns3],
  [/upright/i, RectangleVertical],
  [/chest/i, Archive],
  [/^freezers/i, Snowflake],
  [/wine/i, Wine],
  [/beverage/i, CupSoda],
  [/kegerator/i, Beer],
  [/ice maker/i, Cuboid],
  [/water filter/i, Droplets],
]
const wikiIcon = (name: string): [LucideIcon, string | undefined] => { const hit = WIKI_ICON.find(([re]) => re.test(name)); return hit ? [hit[1], hit[2]] : [Refrigerator, undefined] }

// v2's five tile tints, cycled, each with an ink dark enough for the icon.
const LETTER_TONES = [
  'from-[#eef4ff] to-[#d9e7ff] text-[#0454c0] ring-[#0469f0]/15',
  'from-[#e6f7ff] to-[#c9edfb] text-[#0e7490] ring-[#0891b2]/15',
  'from-[#fff1e6] to-[#ffdfc4] text-[#b54708] ring-[#ff9951]/25',
  'from-[#fbeef2] to-[#f7d9e4] text-[#c11574] ring-[#c11574]/15',
  'from-[#eef9f1] to-[#d3f1de] text-[#067647] ring-[#10a65c]/20',
]

export function Wiki({ model }: { model: Model }) {
  const m = model.wiki
  return (
    <Section aria={m.aria} v2="V20" tone="mist">
      <SectionHeader head={m.head} tone="mist" />
      <ul className={compactClassName("grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5")}>
        {m.items.map((it, i) => {
          const [I, extra] = wikiIcon(it.name)
          return (
          <li key={it.href}>
            <DestinationLink href={it.href} className="group block h-full rounded-2xl">
              <Card className="relative h-full gap-0! overflow-hidden rounded-2xl! py-0! transition duration-200 group-hover:-translate-y-0.5 group-hover:border-neutral-300 group-hover:shadow-md">
                {/* A product of this type from the page, so each entry shows what it names (owner, 10/15/2026). */}
                {it.img ? (
                  <span className={compactClassName("relative block aspect-[5/4] border-b border-neutral-100 bg-white")}>
                    <Img src={it.img.src} alt="" className="absolute inset-0 size-full p-3 transition-transform duration-300 group-hover:scale-[1.04]" />
                  </span>
                ) : null}
                <span className={compactClassName(cn('flex items-center gap-3 px-4 py-3', !it.img && 'pt-4'))}>
                <span
                  aria-hidden="true"
                  className={compactClassName(cn('flex size-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ring-1 ring-inset transition-transform duration-200 group-hover:scale-105', LETTER_TONES[i % LETTER_TONES.length]))}
                >
                  <I className={cn('size-[18px]', extra)} />
                </span>
                <span className={compactClassName("pr-6 text-[15px] font-semibold leading-snug text-neutral-900 group-hover:text-[#0454c0]")}>{it.name}</span>
                </span>
                <ArrowRight aria-hidden="true" className="absolute right-4 bottom-4 size-4 text-neutral-300 transition duration-200 group-hover:translate-x-0.5 group-hover:text-[#0454c0]" />
              </Card>
            </DestinationLink>
          </li>
          )
        })}
      </ul>
    </Section>
  )
}

/* ------------------------------------------------------------------ V20b FIN, FPN, FFN and FIS */

type IdTone = { variant: 'outline' | 'orange'; badge: string; ring: string }
// Each identifier keeps its own accent: FIN the brand blue, FPN the brand orange, FFN water-filter cyan, FIS local green.
const ID_TONES: Record<string, IdTone> = {
  FIN: { variant: 'outline', badge: 'border-[#0469f0]/30! bg-[#eef5ff]! text-[#0454c0]!', ring: 'hover:ring-[#0469f0]/25' },
  FPN: { variant: 'orange', badge: 'border-[#ff9951]!', ring: 'hover:ring-[#ff9951]/40' },
  FFN: { variant: 'outline', badge: 'border-[#0891b2]/30! bg-[#e8f7fb]! text-[#0e7490]!', ring: 'hover:ring-[#0891b2]/25' },
  FIS: { variant: 'outline', badge: 'border-[#10a65c]/30! bg-[#ecfdf5]! text-[#067647]!', ring: 'hover:ring-[#10a65c]/30' },
}
const ID_ORDER = ['FIN', 'FPN', 'FFN', 'FIS']

export function Ids({ model }: { model: Model }) {
  const m = model.ids
  return (
    <Section aria={m.aria} v2="V20b" tone="white" className="bg-gradient-to-b from-[#f3f8ff] via-white to-white">
      <SectionHeader head={{ title: m.title, id: m.id, sub: m.sub, viewAll: m.viewAll }} tone="white" />
      <ul className={compactClassName("grid gap-5 sm:grid-cols-2 lg:grid-cols-4")}>
        {m.cards.map((c, i) => {
          const tone = ID_TONES[c.code] ?? ID_TONES[ID_ORDER[i % ID_ORDER.length]]
          return (
            <li key={c.code + c.link.href}>
              <Card className={cn('group relative h-full gap-0! overflow-hidden py-0! transition duration-200 hover:-translate-y-0.5 hover:shadow-md hover:ring-2', tone.ring)}>
                <div className={compactClassName("relative aspect-[16/10] overflow-hidden bg-neutral-100")}>
                  <Img src={c.img.src} alt={c.img.alt} fit="cover" className="size-full transition duration-500 group-hover:scale-[1.04]" />
                  <div aria-hidden="true" className={compactClassName("absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white/90 to-transparent")} />
                </div>
                <div className={compactClassName("flex flex-1 flex-col px-5 pb-5")}>
                  <Badge
                    variant={tone.variant}
                    className={cn('relative -mt-5 rounded-lg! px-3! py-1! font-mono text-lg! font-extrabold! tracking-[0.08em] shadow-sm ring-4 ring-white', tone.badge)}
                  >
                    {c.code}
                  </Badge>
                  <h3 className={compactClassName("mt-3 text-lg font-semibold leading-snug tracking-tight text-neutral-950")}>{c.q}</h3>
                  <p className={compactClassName("mt-2 text-sm leading-relaxed text-neutral-600")}>{c.a}</p>
                  {/* The link stretches over the whole card (after:inset-0), so the card is one click target. */}
                  <DestinationLink
                    href={c.link.href}
                    aria-label={c.link.aria}
                    className="mt-auto inline-flex items-center gap-1.5 pt-4 text-sm font-semibold text-[#0454c0] after:absolute after:inset-0 after:rounded-xl hover:underline hover:underline-offset-2"
                  >
                    {words(c.link.text)}
                    <ArrowRight aria-hidden="true" className="size-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </DestinationLink>
                </div>
              </Card>
            </li>
          )
        })}
      </ul>
    </Section>
  )
}

/* ------------------------------------------------------------------ V21 guides and research */

// v2's crop for each guide photo (several are portrait shots shown in a wide frame).
const GUIDE_FOCUS: Record<string, string> = {
  '/energy-efficiency-guide': '50% 58%',
  '/refrigerator-buying-guide': '50% 45%',
  '/freezer-buying-guide': '50% 42%',
  '/best-specialty-appliances': '50% 45%',
  '/food-storage-appliances': '50% 60%',
}

const RESEARCH_TONES = [
  { card: 'from-[#eaf2ff] via-[#f5f9ff] to-white border-[#0469f0]/15', pill: 'border-[#0469f0]/20! bg-[#0469f0]/10! text-[#0454c0]!', icon: 'text-[#0469f0]' },
  { card: 'from-[#fff0e3] via-[#fff7f0] to-white border-[#ff9951]/25', pill: 'border-[#ff9951]/30! bg-[#ff9951]/15! text-[#b54708]!', icon: 'text-[#ff9951]' },
  { card: 'from-[#e3f6ea] via-[#f0faf4] to-white border-[#10a65c]/20', pill: 'border-[#10a65c]/25! bg-[#10a65c]/10! text-[#067647]!', icon: 'text-[#10a65c]' },
]
function researchIcon(eyebrow: string) {
  if (/dimension/i.test(eyebrow)) return Ruler
  if (/lifespan|repair/i.test(eyebrow)) return Hourglass
  if (/rebate/i.test(eyebrow)) return BadgePercent
  return FileText
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
/** The model's date text, as a <time> with a machine-readable date when it parses ("February 28, 2026"). */
function DateText({ text }: { text: string }) {
  const d = /^([A-Z][a-z]+) (\d{1,2}), (\d{4})$/.exec(text)
  const mo = d ? MONTHS.indexOf(d[1]) + 1 : 0
  if (!d || !mo) return <span>{text}</span>
  return <time dateTime={`${d[3]}-${String(mo).padStart(2, '0')}-${d[2].padStart(2, '0')}`}>{text}</time>
}
const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase()

export function Guides({ model }: { model: Model }) {
  const m = model.guides
  const [startHead, researchHead, latestHead] = m.subheads
  return (
    <Section aria={m.aria} v2="V21" tone="sand">
      <SectionHeader head={m.head} tone="sand" />

      {startHead ? <SubHeader title={startHead} /> : null}
      <ul className={compactClassName("grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4")}>
        {m.start.map((g) => (
          <li key={g.href}>
            <DestinationLink href={g.href} className="group block h-full rounded-2xl">
              <Card className="h-full gap-0! overflow-hidden rounded-2xl! py-0! transition duration-200 group-hover:-translate-y-0.5 group-hover:shadow-md">
                <div className={compactClassName("relative aspect-[16/10] overflow-hidden bg-[#f1ebe0]")}>
                  <Img
                    src={g.img.src}
                    alt={g.img.alt}
                    fit="cover"
                    style={{ objectPosition: GUIDE_FOCUS[g.href] }}
                    className="size-full transition duration-500 group-hover:scale-[1.04]"
                  />
                </div>
                <div className={compactClassName("flex flex-1 flex-col gap-1.5 p-4")}>
                  <h3 className={compactClassName("text-pretty text-[15px] font-semibold leading-snug text-neutral-950 group-hover:text-[#0454c0]")}>{g.title}</h3>
                  <p className={compactClassName("text-[13px] leading-relaxed text-neutral-600")}>{g.text}</p>
                  <More text={g.more} className="mt-auto pt-2 text-[13px]" />
                </div>
              </Card>
            </DestinationLink>
          </li>
        ))}
      </ul>

      {researchHead ? <SubHeader title={researchHead} className="mt-8 md:mt-10" /> : null}
      <ul className={compactClassName("grid gap-4 lg:grid-cols-3")}>
        {m.research.map((r, i) => {
          const t = RESEARCH_TONES[i % RESEARCH_TONES.length]
          const Icon = researchIcon(r.eyebrow)
          return (
            <li key={r.href} className={compactClassName("min-w-0")}>
              <DestinationLink href={r.href} className="group block h-full rounded-2xl">
                <Card className={cn('relative h-full gap-0! overflow-hidden rounded-2xl! bg-gradient-to-br px-6 py-6! transition duration-200 group-hover:-translate-y-1 group-hover:shadow-lg', t.card)}>
                  <Icon
                    aria-hidden="true"
                    strokeWidth={1.5}
                    className={cn('pointer-events-none absolute -right-5 -bottom-5 size-32 opacity-[0.13] transition-transform duration-500 group-hover:-rotate-6 group-hover:scale-110', t.icon)}
                  />
                  <Badge variant="outline" className={cn('relative max-w-full gap-1.5! rounded-full! px-3! py-1! text-xs! font-semibold! whitespace-normal!', t.pill)}>
                    <Icon aria-hidden="true" />
                    {r.eyebrow}
                  </Badge>
                  <h3 className={compactClassName("relative mt-4 text-balance text-2xl font-bold leading-[1.15] tracking-tight text-neutral-950 xl:text-[1.7rem]")}>{r.title}</h3>
                  <p className={compactClassName("relative mt-3 text-[15px] leading-relaxed text-neutral-700")}>{r.text}</p>
                  <More text={r.more} className="relative mt-auto pt-6 text-sm" />
                </Card>
              </DestinationLink>
            </li>
          )
        })}
      </ul>

      {latestHead ? <SubHeader title={latestHead} className="mt-8 md:mt-10" /> : null}
      <ul className={compactClassName("grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4")}>
        {m.latest.map((a) => (
          <li key={a.href}>
            <DestinationLink href={a.href} className="group block h-full rounded-2xl">
              <Card className="h-full gap-0! overflow-hidden rounded-2xl! py-0! transition duration-200 group-hover:-translate-y-0.5 group-hover:shadow-md">
                <div className={compactClassName("relative aspect-video overflow-hidden bg-neutral-100")}>
                  <Img src={a.img.src} alt={a.img.alt} fit="cover" className="size-full transition duration-500 group-hover:scale-[1.04]" />
                  <Badge variant="orange" className="absolute bottom-3 left-3 rounded-full! px-2.5! text-[11px]! font-semibold! uppercase tracking-[0.08em] shadow-sm">
                    {a.tag}
                  </Badge>
                </div>
                <div className={compactClassName("flex flex-1 flex-col p-4")}>
                  <h3 className={compactClassName("text-pretty text-[15px] font-semibold leading-snug text-neutral-950 group-hover:text-[#0454c0]")}>{a.title}</h3>
                  <div className={compactClassName("mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-neutral-600")}>
                    {/* shadcn Avatar with the photo drawn by Img: Radix AvatarImage renders nothing on the server,
                        which would drop the author photo from the static HTML. */}
                    <Avatar className="size-6! bg-white ring-1 ring-black/10">
                      {a.author.img ? (
                        <Img src={a.author.img.src} alt={a.author.img.alt} fit="cover" className="size-full" />
                      ) : (
                        <AvatarFallback aria-hidden="true" className="text-[10px] font-semibold text-neutral-700">{initials(a.author.name)}</AvatarFallback>
                      )}
                    </Avatar>
                    <span className={compactClassName("font-medium text-neutral-800")}>{a.author.name}</span>
                    <span aria-hidden="true" className={compactClassName("text-neutral-300")}>•</span>
                    <DateText text={a.date} />
                  </div>
                  <p className={compactClassName("mt-3 text-[13px] leading-relaxed text-neutral-600")}>{a.text}</p>
                </div>
              </Card>
            </DestinationLink>
          </li>
        ))}
      </ul>

      <ChipRows rows={m.chiprows} />
    </Section>
  )
}
